import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactNode, createContext, useCallback, useContext } from 'react';

import { SchoolId } from '@suntex.fim/api-client';
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react';
import { LoginType } from '@suntex.fim/enums';
import { useFramework } from '@suntex.fim/framework';

import { SchoolContext as ISchoolContext } from './types';

const SchoolContext = createContext<ISchoolContext | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useSchoolContext(): ISchoolContext {
  const context = useContext(SchoolContext);
  if (context === null) {
    throw new Error('useSchoolContext must be used within a SchoolProvider');
  }
  return context;
}

/** Returns the SchoolContext value, or `null` when no SchoolProvider is present. */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalSchoolContext(): ISchoolContext | null {
  return useContext(SchoolContext);
}

export interface SchoolProviderProps {
  children: ReactNode;
  schoolId?: SchoolId;
}

interface SchoolProviderCoreProps {
  children: ReactNode;
  schoolId: SchoolId;
}

const isValidId = (id: number): boolean => id > 0;

function SchoolProviderCore({ children, schoolId }: SchoolProviderCoreProps) {
  const { api } = useFramework();
  const { login } = useAuth(isAuthenticated);
  const queryClient = useQueryClient();

  const { data: schoolInfo, isLoading: isSchoolInfoLoading } = useQuery({
    queryKey: [
      'school',
      'info',
      schoolId,
      login.id,
      login.loginType,
      login.userId,
    ],
    queryFn: async () => {
      return await api.get_School_Index({
        loginType: login.loginType,
        loginId: login.id,
        schoolId,
        userId: login.userId,
      });
    },
    enabled: isValidId(schoolId) && login.loginType !== LoginType.StudentPlayer,
  });

  const reload = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['school', 'info', schoolId],
    });
  }, [queryClient, schoolId]);

  const value: ISchoolContext = {
    schoolId: isValidId(schoolId) ? schoolId : null,
    schoolInfo: schoolInfo ?? null,
    isLoading: isSchoolInfoLoading,
    reload,
  };

  return (
    <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>
  );
}

/**
 * Loads school data based on the current login (or prop override) and exposes
 * it via `useSchool` and per-data hooks. The query is skipped when the id
 * isn't valid (`<= 0`). Automatically resets when the resolved id changes, so
 * consumers never see stale data from a previous entity.
 */
export function SchoolProvider({
  children,
  schoolId: schoolIdProp,
}: SchoolProviderProps) {
  const { login } = useAuth(isAuthenticated);
  const schoolId = schoolIdProp ?? login.schoolId;

  return (
    <SchoolProviderCore key={schoolId} schoolId={schoolId}>
      {children}
    </SchoolProviderCore>
  );
}
